import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filters } from './Filters';
import { SkinGrid } from './SkinGrid';
import type { FilterState } from '@/types';
import { marketApi, type MarketItem } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowUpDown, Grid3X3, List, TrendingUp, Star, Loader2, Filter, Search } from 'lucide-react';
import { toast } from 'sonner';
import { loadSkinsData } from '@/lib/skinsData';
import { LiveTicker } from '@/components/LiveTicker';

type SortOption = 'price_asc' | 'price_desc' | 'discount' | 'popular';

interface MarketPageProps {
  // isAuthenticated больше не нужен - карточки теперь ссылки
}

const ITEMS_PER_PAGE = 50;

export function MarketPage(_props: MarketPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Инициализируем поиск из URL-параметра ?search= (например, переход из хлебной крошки)
  const searchFromUrl = searchParams.get('search') || '';

  const [filters, setFilters] = useState<FilterState>({
    qualities: [],
    rarities: [],
    weapons: [],
    search: searchFromUrl,
    statTrakOnly: false,
    priceFrom: undefined,
    priceTo: undefined,
    categories: [],
    phases: [],
    delivery: '',
    hasSticker: false,
    hasKeychain: false,
    floatFrom: undefined,
    floatTo: undefined,
  });
  
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Поиск: локальный input с дебаунсом 350ms → обновляет filters.search
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
      }
    }, 350);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);
  
  const [items, setItems] = useState<MarketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  const isFirstRender = useRef(true);

  // Load items from server
  const loadItems = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const apiFilters: any = {
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        sort: sortBy,
      };
      
      if (filters.search) apiFilters.search = filters.search;
      if (filters.priceFrom !== undefined) apiFilters.min_price = filters.priceFrom;
      if (filters.priceTo !== undefined) apiFilters.max_price = filters.priceTo;
      if (filters.statTrakOnly) apiFilters.statTrak = true;
      // Фильтры по данным расширенного прайса
      if (filters.categories.length > 0) apiFilters.categories = filters.categories.join(',');
      if (filters.phases.length > 0) apiFilters.phases = filters.phases.join(',');
      if (filters.hasSticker) apiFilters.has_sticker = true;
      if (filters.hasKeychain) apiFilters.has_keychain = true;
      if (filters.delivery) apiFilters.delivery = filters.delivery;
      if (filters.floatFrom !== undefined) apiFilters.float_min = filters.floatFrom;
      if (filters.floatTo !== undefined) apiFilters.float_max = filters.floatTo;
      
      if (filters.weapons.length > 0) {
        apiFilters.weapons = filters.weapons.join(',');
      }
      if (filters.rarities.length > 0) {
        apiFilters.rarities = filters.rarities.join(',');
      }
      if (filters.qualities.length > 0) {
        apiFilters.qualities = filters.qualities.join(',');
      }

      const response = await marketApi.getItems(apiFilters);
      
      const newItems = response.items || [];
      setTotalCount(response.total || 0);
      
      if (reset) {
        setItems(newItems);
      } else {
        setItems(prev => [...prev, ...newItems]);
      }
      
      setHasMore(newItems.length === ITEMS_PER_PAGE && newItems.length > 0);
    } catch (error: any) {
      toast.error('Ошибка загрузки предметов: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters, sortBy]);

  // Initial load
  useEffect(() => {
    loadSkinsData().then(() => loadItems(1, true));
  }, []);

  // Reload when filters or sort change (skip first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    setPage(1);
    setHasMore(true);
    loadItems(1, true);
  }, [filters, sortBy]);

  // Load more handler
  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    
    const nextPage = page + 1;
    setPage(nextPage);
    loadItems(nextPage, false);
  }, [page, loading, loadingMore, hasMore, loadItems]);

  const sortOptions: { value: SortOption; label: string; icon: React.ReactNode }[] = [
    { value: 'popular', label: 'Популярные', icon: <Star className="w-4 h-4" /> },
    { value: 'price_asc', label: 'Цена ↑', icon: <ArrowUpDown className="w-4 h-4" /> },
    { value: 'price_desc', label: 'Цена ↓', icon: <ArrowUpDown className="w-4 h-4 rotate-180" /> },
    { value: 'discount', label: 'По количеству', icon: <TrendingUp className="w-4 h-4" /> },
  ];

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col lg:flex-row gap-6 max-w-[1920px] mx-auto">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden flex items-center justify-between mb-2">
          <Button
            variant="outline"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="border-[#30363d] text-[#8b949e]"
          >
            <Filter className="w-4 h-4 mr-2" />
            Фильтры
            {(filters.qualities.length + filters.rarities.length + filters.weapons.length) > 0 && (
              <Badge className="ml-2 bg-[#ff6b00] text-white">
                {filters.qualities.length + filters.rarities.length + filters.weapons.length}
              </Badge>
            )}
          </Button>
        </div>

        {/* Sidebar Filters */}
        <aside className={`lg:w-64 flex-shrink-0 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
          <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:scrollbar-dark">
            <Filters filters={filters} onFiltersChange={setFilters} />
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <LiveTicker />
          {/* Search Bar — над сеткой товаров */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8b949e] pointer-events-none" />
            <Input
              placeholder="Поиск скинов..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 bg-[#161b22] border-[#30363d] text-white placeholder:text-[#8b949e] focus:border-[#ff6b00] focus:ring-[#ff6b00]/20 h-11"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b949e] hover:text-white transition-colors text-lg leading-none"
              >
                ×
              </button>
            )}
          </div>

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 p-4 bg-[#161b22] rounded-xl border border-[#30363d]">
            <div className="flex items-center gap-2">
              <span className="text-[#8b949e] text-sm">Найдено:</span>
              <Badge className="bg-[#ff6b00]/20 text-[#ff6b00] border-[#ff6b00]/30">
                {loading ? '...' : totalCount}
              </Badge>
              <span className="text-[#8b949e] text-xs ml-2">
                {items.length > 0 && `(показано ${items.length})`}
              </span>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              {/* Sort Dropdown */}
              <div className="flex items-center gap-1 bg-[#0d1117] rounded-lg p-1 border border-[#30363d] flex-1 sm:flex-none overflow-x-auto">
                {sortOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant="ghost"
                    size="sm"
                    onClick={() => setSortBy(option.value)}
                    disabled={loading}
                    className={`text-xs whitespace-nowrap ${
                      sortBy === option.value
                        ? 'bg-[#ff6b00] text-white hover:bg-[#ff8533]'
                        : 'text-[#8b949e] hover:text-white hover:bg-[#1a2332]'
                    }`}
                  >
                    {option.icon}
                    <span className="ml-1 hidden sm:inline">{option.label}</span>
                  </Button>
                ))}
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center bg-[#0d1117] rounded-lg p-1 border border-[#30363d] flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={`${
                    viewMode === 'grid'
                      ? 'bg-[#ff6b00] text-white'
                      : 'text-[#8b949e] hover:text-white'
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`${
                    viewMode === 'list'
                      ? 'bg-[#ff6b00] text-white'
                      : 'text-[#8b949e] hover:text-white'
                  }`}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#ff6b00] animate-spin" />
            </div>
          )}

          {/* Skin Grid */}
          {!loading && (
            <>
              <SkinGrid 
                skins={items} 
                viewMode={viewMode}
              />
              
              {/* Load More Button */}
              {hasMore && items.length > 0 && (
                <div className="flex items-center justify-center py-8">
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="border-[#30363d] text-[#8b949e] hover:text-white hover:bg-[#1a2332]"
                  >
                    {loadingMore ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    Загрузить ещё
                  </Button>
                </div>
              )}
              
              {/* End of List */}
              {!hasMore && items.length > 0 && (
                <div className="text-center py-8 text-[#8b949e] text-sm">
                  Показаны все предметы ({items.length} из {totalCount})
                </div>
              )}
              
              {/* Empty State */}
              {items.length === 0 && !loading && (
                <div className="text-center py-20">
                  <p className="text-[#8b949e] text-lg">Предметы не найдены</p>
                  <p className="text-[#8b949e] text-sm mt-2">Попробуйте изменить фильтры</p>
                  <Button 
                    variant="outline" 
                    onClick={() => { setSearchParams({}); setFilters({
                      qualities: [],
                      rarities: [],
                      weapons: [],
                      search: '',
                      statTrakOnly: false,
                      categories: [],
                      phases: [],
                      delivery: '',
                      hasSticker: false,
                      hasKeychain: false,
                      floatFrom: undefined,
                      floatTo: undefined,
                      priceFrom: undefined,
                      priceTo: undefined,
                    }); }}
                    className="mt-4 border-[#30363d] text-[#8b949e]"
                  >
                    Сбросить фильтры
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
